import { useState, useEffect, useRef } from "react";
import { useSignedUrls } from "@/components/SignedImage";
import { uploadInquiryPhoto } from "@/lib/storageUtils";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, Calendar, MapPin, Ruler, FileText,
  CheckCircle2, UserCheck, Upload, X, Loader2, Menu, User, AlertTriangle, Send, Lock, Unlock
} from "lucide-react";
import Logo from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { AppSidebar } from "@/components/AppSidebar";
import { useProjects } from "@/hooks/useProjects";
import { isFacadeServiceSlug, resolveSlug } from "@/config/serviceCatalog";
import type { WallSummaryData } from "@/services/fasady/WallAccordion";
import { getServiceModule } from "@/services/registry";
import type { ServiceModule } from "@/services/types";
import { GenericIntakeForm, type IntakeField } from "@/components/GenericIntakeForm";
import { useI18n } from "@/lib/i18n";

interface InquiryData {
  id: string;
  title: string;
  description: string | null;
  region: string | null;
  status: string;
  created_at: string;
  photos: string[] | null;
  engine_id: string | null;
  intake_data: Record<string, unknown>;
}

interface Quote {
  id: string;
  inquiry_id: string;
  supplier_id: string;
  total_price: number;
  notes: string | null;
  customer_approved: boolean;
  created_at: string;
}

interface SupplierInfo {
  id: string;
  company_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  region: string | null;
}

interface FacadeLiveEstimate {
  area: number;
  baseLow: number;
  baseHigh: number;
  totalLow: number;
  totalHigh: number;
}

const MONTH_NAMES = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čvn", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"];

function parseAreaValue(value: string | null | undefined): number {
  if (!value) return 0;
  const match = value.match(/(\d+[.,]?\d*)/);
  return match ? parseFloat(match[1].replace(",", ".")) : 0;
}

function formatPriceRange(low: number, high: number): string {
  return `${low.toLocaleString("cs-CZ")} – ${high.toLocaleString("cs-CZ")} Kč`;
}

function parsePriceRange(value: string | null | undefined): { low: number; high: number } | null {
  if (!value) return null;
  const match = value.match(/(\d[\d\s]*)\s*[–-]\s*(\d[\d\s]*)/);
  if (!match) return null;
  return {
    low: parseInt(match[1].replace(/\s/g, ""), 10),
    high: parseInt(match[2].replace(/\s/g, ""), 10),
  };
}

function getFacadeLiveEstimate(
  details: Record<string, string>,
  wallsData: WallSummaryData[],
  wallsTotalArea: number
): FacadeLiveEstimate | null {
  let area = wallsTotalArea;
  if (area <= 0) {
    area = parseAreaValue(details["Plocha"] || details["Plocha fasády"] || "");
  }
  if (area <= 0) return null;

  const materialRaw = (wallsData.find((wall) => wall.material)?.material || details["Materiál"] || details["Material"] || "").toLowerCase();
  const thicknessRaw =
    wallsData.find((wall) => wall.thickness_cm)?.thickness_cm ||
    parseInt(details["Tloušťka"] || details["Tloušťka izolace"] || "18", 10) ||
    18;

  const isVentilated =
    materialRaw.includes("provětráv") ||
    materialRaw.includes("dřev") ||
    materialRaw.includes("plech") ||
    materialRaw.includes("hliník") ||
    materialRaw.includes("hpl") ||
    materialRaw.includes("vlákno") ||
    materialRaw.includes("klinker");

  let baseLow = 1700;
  let baseHigh = 2400;
  if (isVentilated) {
    baseLow = 2500;
    baseHigh = 4000;
  } else if (materialRaw.includes("bílý") || materialRaw.includes("bily") || materialRaw.includes("eps")) {
    baseLow = 1500;
    baseHigh = 2100;
  } else if (materialRaw.includes("šed") || materialRaw.includes("grafit")) {
    baseLow = 1700;
    baseHigh = 2400;
  } else if (materialRaw.includes("vat") || materialRaw.includes("minerál") || materialRaw.includes("mw")) {
    baseLow = 2000;
    baseHigh = 2800;
  } else if (materialRaw.includes("pir")) {
    baseLow = 2200;
    baseHigh = 3000;
  } else if (materialRaw.includes("xps")) {
    baseLow = 1800;
    baseHigh = 2500;
  }

  if (thicknessRaw >= 22) {
    baseLow += 300;
    baseHigh += 300;
  } else if (thicknessRaw >= 18) {
    baseLow += 150;
    baseHigh += 150;
  }

  return {
    area,
    baseLow,
    baseHigh,
    totalLow: Math.round(area * baseLow),
    totalHigh: Math.round(area * baseHigh),
  };
}

function RealizationMonthGrid({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const years = [currentYear, currentYear + 1];

  const toggle = (key: string) => {
    onChange(selected.includes(key) ? selected.filter((s) => s !== key) : [...selected, key]);
  };

  const isPast = (year: number, monthIdx: number) =>
    year < currentYear || (year === currentYear && monthIdx < currentMonth);

  return (
    <div className="space-y-3">
      {years.map((year) => (
        <div key={year}>
          <p className="text-xs font-medium text-muted-foreground mb-1.5">{year}</p>
          <div className="grid grid-cols-6 gap-1.5">
            {MONTH_NAMES.map((name, idx) => {
              const key = `${year}-${String(idx + 1).padStart(2, "0")}`;
              const past = isPast(year, idx);
              const isSelected = selected.includes(key);
              return (
                <button
                  key={key}
                  disabled={past}
                  onClick={() => toggle(key)}
                  className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                    past
                      ? "text-muted-foreground/30 cursor-not-allowed"
                      : isSelected
                        ? "bg-primary text-primary-foreground"
                        : "border border-border text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function InquiryDetail() {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const { user, isReady } = useAuth();
  const { lang } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const { sites, chats, deleteChat, assignChat, addSite } = useProjects();

  // Guest mode: check URL ?token= param first (from email link), then localStorage
  const guestToken = (() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get("token");

      const tokens = JSON.parse(localStorage.getItem("guest_inquiry_tokens") || "{}");
      const storedToken = inquiryId ? tokens[inquiryId] || null : null;

      const token = urlToken || storedToken;

      // Persist URL token to localStorage so it works on subsequent visits
      if (urlToken && inquiryId && !storedToken) {
        tokens[inquiryId] = urlToken;
        localStorage.setItem("guest_inquiry_tokens", JSON.stringify(tokens));
        try {
          const ids = JSON.parse(localStorage.getItem("guest_inquiry_ids") || "[]");
          if (!ids.includes(inquiryId)) {
            ids.push(inquiryId);
            localStorage.setItem("guest_inquiry_ids", JSON.stringify(ids));
          }
        } catch {}
      }

      // Clean token from URL bar for security
      if (urlToken) {
        window.history.replaceState({}, "", window.location.pathname);
      }

      return token;
    } catch { return null; }
  })();
  const isGuest = !user && !!guestToken;

  const [inquiry, setInquiry] = useState<InquiryData | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [supplierInfo, setSupplierInfo] = useState<Record<string, SupplierInfo>>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [approvingQuoteId, setApprovingQuoteId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [realizationMonths, setRealizationMonths] = useState<string[]>([]);
  const [wallsTotalArea, setWallsTotalArea] = useState(0);
  const [wallsData, setWallsData] = useState<WallSummaryData[]>([]);
  const [serviceModule, setServiceModule] = useState<ServiceModule | null>(null);
  const [intakeSchema, setIntakeSchema] = useState<IntakeField[]>([]);
  const [intakeValues, setIntakeValues] = useState<Record<string, unknown>>({});
  const [savingIntake, setSavingIntake] = useState(false);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const [showFloatingBtn, setShowFloatingBtn] = useState(true);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const submitRef = useRef<HTMLDivElement>(null);

  // Guest contact fields
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  // Generate signed URLs for photos
  const signedPhotos = useSignedUrls(inquiry?.photos || []);

  useEffect(() => {
    if (!isReady) return; // Wait for auth to initialize before deciding
    if (!user && !guestToken) {
      navigate("/login");
      return;
    }
    if (inquiryId) loadData();
  }, [user, isReady, inquiryId]);

  useEffect(() => {
    const el = submitRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFloatingBtn(!entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  const loadData = async () => {
    if (!inquiryId) return;

    let inq: InquiryData | null = null;

    if (isGuest) {
      // Guest: load via edge function
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/guest-inquiry`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "get", inquiry_id: inquiryId, token: guestToken, lang }),
          }
        );
        if (!resp.ok) { toast.error("Poptávka nenalezena"); navigate("/"); return; }
        const result = await resp.json();
        inq = result.inquiry;
      } catch {
        toast.error("Chyba při načítání"); navigate("/"); return;
      }
    } else {
      // Logged-in: load via Supabase client
      const { data, error } = await supabase
        .from("inquiries")
        .select("id, title, description, region, status, created_at, photos, engine_id, intake_data")
        .eq("id", inquiryId)
        .single();

      if (error || !data) {
        toast.error("Poptávka nenalezena");
        navigate("/dashboard");
        return;
      }
      inq = data;
    }

    if (!inq) { setLoading(false); return; }
    setInquiry(inq);

    // Load service module for engine-specific UI (customer view).
    // NOTE: This is intentionally NOT plugin-gated. The customer created the inquiry
    // via the service module (e.g. fasádní kalkulačka), so we always render their
    // data with the module's UI regardless of which supplier has the plugin enabled.
    // Plugin gating only applies on the supplier side (SupplierInquiryDetail, SupplierSettings).
    if (inq.engine_id) {
      getServiceModule(inq.engine_id).then(setServiceModule);
      // Fetch intake_schema for generic services (non-custom-module)
      supabase
        .from("services")
        .select("intake_schema")
        .eq("slug", resolveSlug(inq.engine_id))
        .single()
        .then(({ data: svc }) => {
          if (svc?.intake_schema && Array.isArray(svc.intake_schema) && svc.intake_schema.length > 0) {
            setIntakeSchema(svc.intake_schema as IntakeField[]);
          }
        });
    }
    // Set intake_data from inquiry
    if (inq.intake_data && typeof inq.intake_data === "object") {
      setIntakeValues(inq.intake_data as Record<string, unknown>);
    }

    // Parse existing notes/realization from description
    const details = parseDescription(inq.description);
    if (details["Poznámky"]) setNotes(details["Poznámky"]);
    if (details["Termín realizace"]) {
      setRealizationMonths(details["Termín realizace"].split(", ").filter(Boolean));
    }
    // Load guest contact info from description
    if (isGuest) {
      if (details["Jméno"]) setGuestName(details["Jméno"]);
      if (details["E-mail"]) setGuestEmail(details["E-mail"]);
      if (details["Telefon"]) setGuestPhone(details["Telefon"]);
    }

    if (!isGuest) {
      // Load quotes (only for logged-in users)
      const { data: quotesData } = await supabase
        .from("supplier_quotes")
        .select("id, inquiry_id, supplier_id, total_price, notes, customer_approved, created_at")
        .eq("inquiry_id", inquiryId)
        .order("created_at", { ascending: false });

      setQuotes(quotesData || []);

      // Load supplier info
      const supplierIds = new Set((quotesData || []).map(q => q.supplier_id));
      if (supplierIds.size > 0) {
        const { data: suppliers } = await supabase
          .from("suppliers")
          .select("id, company_name, contact_email, contact_phone, region")
          .in("id", Array.from(supplierIds));

        const infoMap: Record<string, SupplierInfo> = {};
        for (const s of suppliers || []) infoMap[s.id] = s;
        setSupplierInfo(infoMap);
      }
    }

    setLoading(false);
  };

  const parseDescription = (desc: string | null): Record<string, string> => {
    if (!desc) return {};
    const result: Record<string, string> = {};
    for (const line of desc.split("\n")) {
      const idx = line.indexOf(":");
      if (idx > 0) {
        const key = line.slice(0, idx).trim();
        const val = line.slice(idx + 1).trim();
        if (val && val !== "null") result[key] = val;
      }
    }
    return result;
  };

  const compressImage = (file: File, maxWidth = 1200, quality = 0.7): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas not supported")); return; }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error("Compression failed")),
          "image/jpeg",
          quality
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !inquiry) return;

    setUploading(true);
    const newPhotos = [...(inquiry.photos || [])];

    for (const file of Array.from(files)) {
      try {
        // Always compress images for smaller storage footprint
        let uploadData: Blob | File = file;
        if (file.type.startsWith("image/")) {
          uploadData = await compressImage(file);
        }

        const path = `${inquiry.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;

        try {
          const storedPath = await uploadInquiryPhoto(path, uploadData);
          newPhotos.push(storedPath);
        } catch (uploadErr: any) {
          toast.error(`Chyba při nahrávání: ${file.name}`);
          continue;
        }
      } catch {
        toast.error(`Chyba při zpracování: ${file.name}`);
      }
    }

    const { error } = await supabase
      .from("inquiries")
      .update({ photos: newPhotos })
      .eq("id", inquiry.id);

    if (!error) {
      setInquiry({ ...inquiry, photos: newPhotos });
      toast.success("Fotky nahrány");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemovePhoto = async (photoUrl: string) => {
    if (!inquiry) return;
    const updated = (inquiry.photos || []).filter(p => p !== photoUrl);
    const { error } = await supabase
      .from("inquiries")
      .update({ photos: updated })
      .eq("id", inquiry.id);

    if (!error) {
      setInquiry({ ...inquiry, photos: updated });
    }
  };

  const handleGuestPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !inquiry || !guestToken) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        let uploadData: Blob | File = file;
        if (file.type.startsWith("image/")) {
          uploadData = await compressImage(file);
        }
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve((reader.result as string).split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(uploadData instanceof Blob ? uploadData : file);
        });
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/guest-inquiry`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "upload_photo",
              inquiry_id: inquiry.id,
              token: guestToken,
              photo_data: base64,
              lang,
            }),
          }
        );
        if (resp.ok) {
          const { url } = await resp.json();
          setInquiry(prev => prev ? { ...prev, photos: [...(prev.photos || []), url] } : prev);
        }
      } catch { toast.error(`Chyba při nahrávání`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSaveDetails = async () => {
    if (!inquiry) return;
    setSavingNotes(true);

    const details = parseDescription(inquiry.description);
    if (notes.trim()) details["Poznámky"] = notes.trim();
    if (realizationMonths.length > 0) details["Termín realizace"] = realizationMonths.join(", ");
    // Guest contact info
    if (isGuest) {
      if (guestName.trim()) details["Jméno"] = guestName.trim();
      if (guestEmail.trim()) details["E-mail"] = guestEmail.trim();
      if (guestPhone.trim()) details["Telefon"] = guestPhone.trim();
    }

    const newDesc = Object.entries(details).map(([k, v]) => `${k}: ${v}`).join("\n");

    if (isGuest) {
      // Guest: save via edge function
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/guest-inquiry`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "update",
              inquiry_id: inquiry.id,
              token: guestToken,
              description: newDesc,
              guest_email: guestEmail.trim() || undefined,
              guest_name: guestName.trim() || undefined,
              guest_phone: guestPhone.trim() || undefined,
              lang,
            }),
          }
        );
        if (resp.ok) {
          setInquiry({ ...inquiry, description: newDesc });
          toast.success("Uloženo");
        } else {
          toast.error("Chyba při ukládání");
        }
      } catch {
        toast.error("Chyba při ukládání");
      }
    } else {
      const { error } = await supabase
        .from("inquiries")
        .update({ description: newDesc })
        .eq("id", inquiry.id);

      if (!error) {
        setInquiry({ ...inquiry, description: newDesc });
        toast.success("Uloženo");
      } else {
        toast.error("Chyba při ukládání");
      }
    }
    setSavingNotes(false);
  };

  const handleApproveQuote = async (quoteId: string) => {
    setApprovingQuoteId(quoteId);
    const { error } = await supabase
      .from("supplier_quotes")
      .update({ customer_approved: true })
      .eq("id", quoteId);

    if (error) {
      toast.error("Chyba při schvalování");
    } else {
      toast.success("Kontakt sdílen s dodavatelem");
      await loadData();
    }
    setApprovingQuoteId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!inquiry) return null;

  const details = parseDescription(inquiry.description);
  const photos = inquiry.photos || [];
  const displayPhotos = signedPhotos.length > 0 ? signedPhotos : photos;
  const isSubmitted = inquiry.status === "submitted";
  const liveEstimate = isFacadeServiceSlug(inquiry.engine_id) ? getFacadeLiveEstimate(details, wallsData, wallsTotalArea) : null;
  const liveEstimateText = liveEstimate ? formatPriceRange(liveEstimate.totalLow, liveEstimate.totalHigh) : null;
  const checkCompleteness = () => {
    const missing: string[] = [];
    // Service-specific completeness check (e.g. fasady requires wall area)
    if (serviceModule?.completenessCheck) {
      missing.push(...serviceModule.completenessCheck({ wallsTotalArea }));
    }
    if (realizationMonths.length === 0) missing.push("termín realizace");
    if (isGuest && !guestEmail.trim()) missing.push("e-mail");
    return missing;
  };

  const handleSubmitInquiry = async () => {
    if (!inquiry) return;
    await handleSaveDetails();
    if (isGuest) {
      // Guest: submit via edge function
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/guest-inquiry`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "update", inquiry_id: inquiry.id, token: guestToken, status: "submitted", lang }),
          }
        );
        if (resp.ok) {
          setInquiry({ ...inquiry, status: "submitted" });
          setShowIncompleteWarning(false);
          toast.success("Poptávka odeslána k nacenění");
          setShowRegisterPrompt(true);
        } else {
          toast.error("Chyba při odesílání");
        }
      } catch { toast.error("Chyba při odesílání"); }
    } else {
      const { error } = await supabase.from("inquiries").update({ status: "submitted" }).eq("id", inquiry.id);
      if (!error) {
        setInquiry({ ...inquiry, status: "submitted" });
        setShowIncompleteWarning(false);
        toast.success("Poptávka odeslána k nacenění");
        // Send summary email via edge function (needs service_role for email)
        try {
          const { data: { session } } = await supabase.auth.getSession();
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/guest-inquiry`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
            body: JSON.stringify({ action: "send_submitted_email", inquiry_id: inquiry.id, lang }),
          });
        } catch { /* email sending is best-effort */ }
      } else {
        toast.error("Chyba při odesílání");
      }
    }
  };

  const handleSubmitClick = () => {
    const missing = checkCompleteness();
    if (missing.length > 0) {
      setShowIncompleteWarning(true);
    } else {
      handleSubmitInquiry();
    }
  };

  const handleUnlockInquiry = async () => {
    if (!inquiry) return;
    const { error } = await supabase.from("inquiries").update({ status: "open" }).eq("id", inquiry.id);
    if (!error) {
      setInquiry({ ...inquiry, status: "open" });
      toast.success("Poptávka odemčena k úpravám");
    }
  };

  return (
    <div className="flex h-[100dvh] bg-background overflow-hidden">
      <AppSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onClose={() => setSidebarOpen(false)}
        isLoggedIn={!!user}
        sites={sites}
        chats={chats}
        onNewChat={() => navigate("/")}
        onSelectChat={() => navigate("/")}
        onDeleteChat={async (id) => { await deleteChat(id); }}
        onAssignChat={async (chatId, siteId) => { await assignChat(chatId, siteId); }}
        onAddSite={async (name) => { await addSite(name); }}
      />

      <div className={`flex-1 flex flex-col h-full overflow-hidden transition-all duration-200 ${sidebarOpen ? "lg:ml-[260px]" : ""}`}>
        <header className="flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-0.5 sm:gap-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`p-1.5 sm:p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground -ml-1 sm:ml-0 ${sidebarOpen ? "lg:hidden" : ""}`}
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" className="flex items-center gap-1 text-foreground text-base hover:opacity-70 transition-opacity">
              <Logo size={32} />
              <span className="font-semibold">cenik.ai</span>
            </Link>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/profile" className="relative p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground">
              <User className="w-5 h-5" />
            </Link>
            <NotificationBell />
          </div>
        </header>

      <div className="flex-1 overflow-y-auto">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Back + title */}
        <div className="flex items-center gap-2 mb-1">
          <button onClick={() => navigate(user ? "/dashboard" : "/")} className="p-2 rounded-full hover:bg-accent transition-colors">
            <ArrowLeft className="w-4 h-4 text-foreground" />
          </button>
          <span className="font-semibold text-foreground text-sm flex-1">{inquiry.title}</span>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
            inquiry.status === "open" ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
          }`}>
            {inquiry.status === "open" ? "Otevřená" : inquiry.status}
          </span>
          <span className="text-[10px] text-muted-foreground">
            · {new Date(inquiry.created_at).toLocaleDateString("cs-CZ")}
          </span>
        </div>


        {/* Offers count card */}
        <div
          onClick={() => document.getElementById("quotes-section")?.scrollIntoView({ behavior: "smooth" })}
          className={`rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity ${
            quotes.length > 0 ? "bg-primary/10 border border-primary/20" : "bg-muted/50 border border-border"
          }`}
        >
          <div className={`text-3xl font-bold ${quotes.length > 0 ? "text-primary" : "text-muted-foreground"}`}>
            {quotes.length}
          </div>
          <div>
            <div className={`text-sm font-semibold ${quotes.length > 0 ? "text-primary" : "text-muted-foreground"}`}>
              {quotes.length === 1 ? "nabídka" : quotes.length < 5 && quotes.length > 0 ? "nabídky" : "nabídek"}
            </div>
            <div className="text-xs text-muted-foreground">
              {quotes.length > 0 ? "od dodavatelů" : "Zatím žádné nabídky – oslovujeme dodavatele"}
            </div>
          </div>
        </div>

        {/* Details + Walls combined */}
        <div className="bg-card border border-border rounded-xl p-4 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Parametry poptávky</h2>
          
          {/* Location row */}
          {inquiry.region && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <span className="text-xs text-muted-foreground">Místo stavby</span>
                <p className="text-sm font-medium text-foreground">{inquiry.region}</p>
              </div>
            </div>
          )}

          {/* Details grid with conflict detection */}
          {(() => {
            const chatArea = details["Plocha"] || details["Plocha fasády"] || null;
            const chatMaterial = details["Materiál"] || details["Material"] || null;
            const chatThickness = details["Tloušťka"] || details["Tloušťka izolace"] || null;
            const priceKey = Object.keys(details).find(k => k.toLowerCase().includes("cen"));
            const priceEstimate = priceKey ? details[priceKey] : null;
            const showStoredPriceEstimate = !liveEstimateText && !!priceEstimate;
            const skipKeys = ["Poznámky", "Telefon", "E-mail", "Termín realizace", "Plocha", "Plocha fasády", "Materiál", "Material", "Tloušťka", "Tloušťka izolace", ...((showStoredPriceEstimate && priceKey) ? [priceKey] : [])];
            const otherDetails = Object.entries(details).filter(([k]) => !skipKeys.includes(k) && !["telefon", "e-mail", "poznámky", "termín"].some(c => k.toLowerCase().includes(c)));

            const hasWalls = wallsData.filter(w => w.area_m2 && w.area_m2 > 0).length > 0;
            const wallMaterials = [...new Set(wallsData.map(w => w.material).filter(Boolean))] as string[];
            const wallThicknesses = [...new Set(wallsData.map(w => w.thickness_cm).filter(Boolean))] as number[];

            const chatAreaNum = parseAreaValue(chatArea);
            const areaConflict = hasWalls && wallsTotalArea > 0 && chatArea && Math.abs(wallsTotalArea - chatAreaNum) >= 1;
            const materialConflict = hasWalls && chatMaterial && wallMaterials.length > 0 &&
              !wallMaterials.some(wm => wm.toLowerCase().includes(chatMaterial.toLowerCase().split(" ")[0]) || chatMaterial.toLowerCase().includes(wm.toLowerCase().split(" ")[0]));
            const thicknessConflict = hasWalls && chatThickness && wallThicknesses.length > 0 &&
              !wallThicknesses.some(wt => chatThickness.includes(String(wt)));
            const parsedStoredPrice = parsePriceRange(priceEstimate);
            const parsedLivePrice = liveEstimateText ? parsePriceRange(liveEstimateText) : null;
            const priceConflict = !!parsedStoredPrice && !!parsedLivePrice &&
              (parsedStoredPrice.low !== parsedLivePrice.low || parsedStoredPrice.high !== parsedLivePrice.high);

            const hasAnyConflict = areaConflict || materialConflict || thicknessConflict || priceConflict;

            // Build conflict list for banner
            const conflicts: { label: string; chatVal: string; wallVal: string; key: string; newVal: string }[] = [];
            if (areaConflict) conflicts.push({ label: "Plocha", chatVal: chatArea!, wallVal: `${wallsTotalArea} m²`, key: chatArea === details["Plocha"] ? "Plocha" : "Plocha fasády", newVal: `${wallsTotalArea} m²` });
            if (materialConflict) conflicts.push({ label: "Materiál", chatVal: chatMaterial!, wallVal: wallMaterials.join(", "), key: chatMaterial === details["Materiál"] ? "Materiál" : "Material", newVal: wallMaterials.join(", ") });
            if (thicknessConflict) conflicts.push({ label: "Tloušťka", chatVal: chatThickness!, wallVal: wallThicknesses.map(t => `${t} cm`).join(", "), key: chatThickness === details["Tloušťka"] ? "Tloušťka" : "Tloušťka izolace", newVal: wallThicknesses.map(t => `${t} cm`).join(", ") });
            if (priceConflict && priceKey && priceEstimate && liveEstimateText) {
              conflicts.push({ label: "Cena", chatVal: priceEstimate, wallVal: liveEstimateText, key: priceKey, newVal: liveEstimateText });
            }

            return (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {chatMaterial && (
                    <div>
                      <span className="text-xs text-muted-foreground">Materiál</span>
                      <p className={`text-sm font-medium ${materialConflict ? "text-muted-foreground line-through" : "text-foreground"}`}>{chatMaterial}</p>
                    </div>
                  )}
                  {chatThickness && (
                    <div>
                      <span className="text-xs text-muted-foreground">Tloušťka</span>
                      <p className={`text-sm font-medium ${thicknessConflict ? "text-muted-foreground line-through" : "text-foreground"}`}>{chatThickness}</p>
                    </div>
                  )}
                  {chatArea && (
                    <div>
                      <span className="text-xs text-muted-foreground">Plocha</span>
                      <p className={`text-sm font-medium ${areaConflict ? "text-muted-foreground line-through" : "text-foreground"}`}>{chatArea}</p>
                    </div>
                  )}
                  {showStoredPriceEstimate && priceEstimate && (
                    <div>
                      <span className="text-xs text-muted-foreground">Cenový odhad</span>
                      <p className={`text-sm font-medium ${priceConflict ? "text-muted-foreground line-through" : "text-foreground"}`}>{priceEstimate}</p>
                    </div>
                  )}
                  {otherDetails.map(([key, val]) => (
                    <div key={key}>
                      <span className="text-xs text-muted-foreground">{key}</span>
                      <p className="text-sm font-medium text-foreground">{val}</p>
                    </div>
                  ))}
                </div>

                {/* Conflict banner */}
                {hasAnyConflict && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                        Údaje ze stěn se liší od odhadu z chatu:
                      </p>
                    </div>
                    <div className="space-y-1 ml-5.5">
                      {conflicts.map(c => (
                        <p key={c.label} className="text-xs text-amber-800 dark:text-amber-300">
                          {c.label}: <span className="line-through">{c.chatVal}</span> → <span className="font-semibold">{c.wallVal}</span>
                        </p>
                      ))}
                    </div>
                    <button
                      onClick={async () => {
                        const updatedDetails = { ...details };
                        for (const c of conflicts) {
                          updatedDetails[c.key] = c.newVal;
                        }
                        const newDesc = Object.entries(updatedDetails).map(([k, v]) => `${k}: ${v}`).join("\n");
                        const { error } = await supabase.from("inquiries").update({ description: newDesc }).eq("id", inquiry.id);
                        if (!error) {
                          setInquiry({ ...inquiry, description: newDesc });
                          toast.success("Parametry aktualizovány podle stěn");
                        }
                      }}
                      className="mt-2 ml-5.5 text-xs font-semibold text-amber-700 dark:text-amber-300 hover:underline"
                    >
                      Aktualizovat parametry podle stěn →
                    </button>
                  </div>
                )}
              </>
            );
          })()}

          {/* Live price estimate inside parameters card */}
          {liveEstimate && (
            <div className="bg-primary/5 border border-primary/15 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">📊 Orientační cena</p>
              <p className="text-xl font-bold text-primary text-center">
                {formatPriceRange(liveEstimate.totalLow, liveEstimate.totalHigh)}
              </p>
              <p className="text-[10px] text-muted-foreground text-center">
                {liveEstimate.baseLow.toLocaleString("cs-CZ")}–{liveEstimate.baseHigh.toLocaleString("cs-CZ")} Kč/m² · {liveEstimate.area} m² · bez DPH
              </p>
            </div>
          )}

          {serviceModule?.InquiryDetailExtras ? (
            <serviceModule.InquiryDetailExtras
              inquiryId={inquiry.id}
              readOnly={isSubmitted}
              showCta={!isSubmitted}
              details={details}
              onTotalAreaChange={setWallsTotalArea}
              onWallsChange={setWallsData}
              guestToken={guestToken}
            />
          ) : intakeSchema.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Ruler className="w-4 h-4" />
                Parametry poptávky
              </h2>
              <GenericIntakeForm
                intakeSchema={intakeSchema}
                values={intakeValues}
                readOnly={isSubmitted}
                saving={savingIntake}
                onChange={(key, value) => setIntakeValues((prev) => ({ ...prev, [key]: value }))}
                onSave={isSubmitted ? undefined : async () => {
                  setSavingIntake(true);
                  try {
                    if (isGuest) {
                      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/guest-inquiry`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ action: "update", inquiry_id: inquiry.id, token: guestToken, intake_data: intakeValues, lang }),
                      });
                    } else {
                      await supabase.from("inquiries").update({ intake_data: intakeValues } as any).eq("id", inquiry.id);
                    }
                    toast.success("Parametry uloženy");
                  } catch {
                    toast.error("Chyba při ukládání");
                  } finally {
                    setSavingIntake(false);
                  }
                }}
              />
            </div>
          )}

        </div>


        {/* Photos */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Fotky domu
          </h2>
          <p className="text-xs text-muted-foreground mb-3">Nahraj fotky celého domu – pomůžou firmám s přesnější nabídkou</p>

          {displayPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {displayPhotos.map((url, i) => (
                <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {!isSubmitted && !isGuest && (
                    <button
                      onClick={() => handleRemovePhoto(photos[i])}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isSubmitted && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={isGuest ? handleGuestPhotoUpload : handlePhotoUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-border rounded-lg text-sm text-muted-foreground hover:bg-accent/50 transition-colors w-full justify-center disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Nahrávám..." : "Nahrát fotky"}
              </button>
            </>
          )}
        </div>

        {/* Realization months */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h2 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Termín realizace
          </h2>
          <p className="text-xs text-muted-foreground mb-3">Označ měsíce, kdy chceš realizovat</p>
          {isSubmitted ? (
            <div className="text-sm text-muted-foreground">
              {realizationMonths.length > 0 ? realizationMonths.join(", ") : "Neuvedeno"}
            </div>
          ) : (
            <RealizationMonthGrid
              selected={realizationMonths}
              onChange={setRealizationMonths}
            />
          )}
        </div>

        {/* Additional notes */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Doplňující informace
          </h2>
          {isSubmitted ? (
            <p className="text-sm text-foreground">{notes || "—"}</p>
          ) : (
            <>
              <textarea
                value={notes}
                onChange={e => {
                  const cleaned = e.target.value
                    .replace(/[\w.-]+@[\w.-]+\.\w+/g, "")
                    .replace(/(\+?\d[\d\s\-()]{7,})/g, "");
                  setNotes(cleaned);
                }}
                placeholder="Počet pater, stáří budovy, stav fasády, speciální požadavky..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Kontaktní údaje (telefon, e-mail) sem prosím neuvádějte – budou sdíleny automaticky po schválení nabídky.</p>
            </>
          )}
        </div>

        {/* Guest contact fields */}
        {isGuest && !isSubmitted && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              Vaše kontaktní údaje
            </h2>
            <div className="space-y-2">
              <input
                type="text"
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                placeholder="Jméno a příjmení"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <input
                type="email"
                value={guestEmail}
                onChange={e => setGuestEmail(e.target.value)}
                placeholder="E-mail *"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <input
                type="tel"
                value={guestPhone}
                onChange={e => setGuestPhone(e.target.value)}
                placeholder="Telefon"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <p className="text-[10px] text-muted-foreground">Kontakt sdílíme s dodavateli až po vašem schválení nabídky.</p>
          </div>
        )}
        {isGuest && isSubmitted && guestEmail && (
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">{guestName || "—"}</p>
                <p className="text-xs text-muted-foreground">{guestEmail}{guestPhone ? ` · ${guestPhone}` : ""}</p>
              </div>
            </div>
          </div>
        )}

        {/* Submit / Lock section - inline below the box */}
        <div id="submit-section" ref={submitRef} className="bg-card border border-border rounded-xl p-4 space-y-3">
          {isSubmitted ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 flex-1">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Poptávka odeslána k nacenění</span>
              </div>
              <button
                onClick={handleUnlockInquiry}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
              >
                <Unlock className="w-3.5 h-3.5" />
                Upravit poptávku
              </button>
            </div>
          ) : (
            <>
              {showIncompleteWarning && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Nemáte vyplněno vše</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Chybí: {checkCompleteness().join(", ")}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowIncompleteWarning(false)}
                      className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
                    >
                      Doplnit údaje
                    </button>
                    <button
                      onClick={handleSubmitInquiry}
                      disabled={savingNotes}
                      className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      Odeslat i tak
                    </button>
                  </div>
                </div>
              )}
              {!showIncompleteWarning && (
                <button
                  onClick={handleSubmitClick}
                  disabled={savingNotes}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-foreground text-background rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Uložit a odeslat poptávku
                </button>
              )}
            </>
          )}
        </div>

        {/* Quotes section - only for logged-in users */}
        {!isGuest && <div id="quotes-section" className="bg-card border border-border rounded-xl p-4">
          <h2 className="text-sm font-semibold text-foreground mb-3">
            Nabídky od firem ({quotes.length})
          </h2>
          {quotes.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Zatím žádné nabídky. Oslovili jsme dodavatele, brzy se ozvou.
            </p>
          ) : (
            <div className="space-y-3">
              {quotes.map((q) => {
                const sup = supplierInfo[q.supplier_id];
                return (
                  <div key={q.id} className="border border-border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">
                        {sup?.company_name || "Dodavatel"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(q.created_at).toLocaleDateString("cs-CZ")}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-lg font-bold text-foreground">
                        {q.total_price.toLocaleString("cs-CZ")} Kč
                      </span>
                      <span className="text-xs text-muted-foreground ml-1">bez DPH</span>
                    </div>
                    {q.notes && (
                      <p className="text-sm text-muted-foreground mb-3 italic">"{q.notes}"</p>
                    )}
                    {q.customer_approved ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-primary">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="font-medium">Schváleno</span>
                        </div>
                        {sup && (
                          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-1">
                            <p className="text-xs font-medium text-foreground">Kontakt na dodavatele</p>
                            {sup.contact_email && <p className="text-sm text-foreground">📧 {sup.contact_email}</p>}
                            {sup.contact_phone && <p className="text-sm text-foreground">📞 {sup.contact_phone}</p>}
                            {sup.region && <p className="text-xs text-muted-foreground">📍 {sup.region}</p>}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => handleApproveQuote(q.id)}
                        disabled={approvingQuoteId === q.id}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                      >
                        <UserCheck className="w-4 h-4" />
                        {approvingQuoteId === q.id ? "..." : "Sdílet kontakt s dodavatelem"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>}
      </div>
      </div>

      {/* Floating scroll-to-submit button - hidden when submit section is visible */}
      {!isSubmitted && showFloatingBtn && (
        <button
          onClick={() => {
            document.getElementById("submit-section")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-3 bg-foreground text-background rounded-full text-sm font-semibold shadow-lg hover:opacity-90 transition-opacity"
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline">Odeslat poptávku</span>
        </button>
      )}

      {/* Guest registration prompt after submission */}
      {showRegisterPrompt && isGuest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-background rounded-2xl p-6 max-w-sm w-full shadow-xl animate-fade-in space-y-4 text-center">
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
            <h2 className="text-lg font-semibold text-foreground">Poptávka odeslána!</h2>
            <p className="text-sm text-muted-foreground">
              Zaregistrujte se a získejte přístup k nabídkám dodavatelů, notifikacím e-mailem a správě všech vašich poptávek na jednom místě.
            </p>
            <Link
              to="/login"
              className="block w-full py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:opacity-90 transition-opacity text-center"
            >
              Zaregistrovat se
            </Link>
            <button
              onClick={() => setShowRegisterPrompt(false)}
              className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Pokračovat bez registrace
            </button>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}
